import React, { useEffect, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-toastify';
import capitecLogo from '../../assets/images/capitec-rise/capitec-logo.png';
import edcLogo from '../../assets/images/capitec-rise/edc-logo.png';
import edcIcon from '../../assets/svg/capitec-rise/edc-icon.svg';
import studentsImage from '../../assets/images/capitec-rise/students.png';
import graduateIcon from '../../assets/svg/capitec-rise/icon-graduate.svg';
import briefsIcon from '../../assets/svg/capitec-rise/icon-briefs.svg';
import earnIcon from '../../assets/svg/capitec-rise/icon-earn.svg';
import mentorshipIcon from '../../assets/svg/capitec-rise/icon-mentorship.svg';
import CapitecRiseApplyModal from './CapitecRiseApplyModal';
import CapitecRiseConfirmationModal from './CapitecRiseConfirmationModal';
import { hasCapitecRiseSubmitted } from '../../utils/capitecRiseSignup';
import './CapitecRise.css';

const ApplyNowButton = ({ variant = 'primary', onClick, disabled = false }) => (
  <span className="capitec-rise-btn-wrap">
    <button
      type="button"
      className={`capitec-rise-btn capitec-rise-btn--${variant}`}
      onClick={() => {
        if (disabled) {
          toast.info('You have already submitted an application.');
          return;
        }
        onClick?.();
      }}
      aria-disabled={disabled}
    >
      Apply now
    </button>
    {disabled ? (
      <span className="capitec-rise-btn-tooltip">
        You have already submitted an application
      </span>
    ) : null}
  </span>
);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SLOW_DROP = { tension: 90, friction: 30, mass: 1.2 };
const SLOW_RISE = { tension: 80, friction: 26, mass: 1.3 };
const SLOW_BOUNCE = { tension: 90, friction: 18, mass: 1.5 };

const BounceIn = ({ children, className, delay = 0, as = 'div' }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const reduced = prefersReducedMotion();
  const Tag = animated[as] || animated.div;
  const style = useSpring({
    transform: reduced || inView ? 'scale(1)' : 'scale(0.8)',
    opacity: reduced || inView ? 1 : 0,
    config: SLOW_BOUNCE,
    delay: reduced || !inView ? 0 : delay,
  });

  return (
    <Tag ref={ref} style={style} className={className}>
      {children}
    </Tag>
  );
};

const BENEFITS = [
  {
    icon: graduateIcon,
    alt: 'Graduation cap',
    label: (
      <>
        10 week
        <br />
        accelerator
      </>
    ),
  },
  {
    icon: briefsIcon,
    alt: 'Computer and live brief',
    label: (
      <>
        Live
        <br />
        briefs
      </>
    ),
  },
  {
    icon: earnIcon,
    alt: 'Institution building',
    label: (
      <>
        Earning
        <br />
        opportunities
      </>
    ),
  },
  {
    icon: mentorshipIcon,
    alt: 'Mentorship',
    label: 'Mentorship',
  },
];

const APPLICANT_SHOULD = [
  'Be based in South Africa',
  'Be an active Capitec client with a Capitec bank account, or willing to open one upon selection',
  'Be between 18–30 years old',
  'Have 2 000–5 000 followers',
  'Have active TikTok or Instagram or YouTube account',
  'Have posted original content consistently over the past 30 days',
  'Be eager to learn and improve',
  'Be committed to completing the full 10-week program',
];

const APPLICANT_MUST_HAVE = [
  'A smartphone capable of filming 1080p video',
  'Reliable access to Wi-Fi or mobile data',
  'Availability to attend all live online sessions',
  'The ability to complete weekly assignments by the required deadlines',
  'A willingness to collaborate with fellow creators, mentors and program facilitators',
];

const CapitecRise = () => {
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const reduced = prefersReducedMotion();

  const headerAnimation = useSpring({
    from: {
      opacity: reduced ? 1 : 0,
      transform: reduced ? 'translateY(0px)' : 'translateY(-50px)',
    },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: SLOW_DROP,
    delay: reduced ? 0 : 200,
  });

  const headingAnimation = useSpring({
    from: {
      opacity: reduced ? 1 : 0,
      transform: reduced ? 'translateY(0px)' : 'translateY(-50px)',
    },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: SLOW_DROP,
    delay: reduced ? 0 : 400,
  });

  const descriptionAnimation = useSpring({
    from: {
      opacity: reduced ? 1 : 0,
      transform: reduced ? 'translateY(0px)' : 'translateY(50px)',
    },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: SLOW_RISE,
    delay: reduced ? 0 : 700,
  });

  const buttonAnimation = useSpring({
    from: {
      opacity: reduced ? 1 : 0,
      transform: reduced ? 'translateY(0px)' : 'translateY(50px)',
    },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: SLOW_RISE,
    delay: reduced ? 0 : 1000,
  });

  const imageAnimation = useSpring({
    from: {
      opacity: reduced ? 1 : 0,
      transform: reduced ? 'translateY(0px)' : 'translateY(50px)',
    },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: SLOW_RISE,
    delay: reduced ? 0 : 550,
  });

  useEffect(() => {
    document.title = 'Capitec Rise | EDC Squared';
    document.body.classList.add('capitec-rise-standalone');
    setHasSubmitted(hasCapitecRiseSubmitted());
    return () => {
      document.title = 'EDC Squared';
      document.body.classList.remove('capitec-rise-standalone');
    };
  }, []);

  return (
    <div className="capitec-rise-page">
      <animated.header
        style={headerAnimation}
        className="capitec-rise-header capitec-rise-inner"
      >
        <div className="capitec-rise-header-left">
          <a
            className="capitec-rise-logo-link"
            href="https://www.capitecbank.co.za"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={capitecLogo}
              alt="Capitec"
              className="capitec-rise-capitec-logo"
            />
          </a>
          <span className="capitec-rise-header-rule" aria-hidden="true" />
          <div className="capitec-rise-header-title">
            <p className="capitec-rise-header-name">Capitec Rise Program</p>
            <p className="capitec-rise-header-powered">Powered by EDC.</p>
          </div>
        </div>
        <a
          className="capitec-rise-logo-link capitec-rise-edc-link"
          href="/"
          aria-label="EDC Squared"
        >
          <img
            src={edcLogo}
            alt=""
            className="capitec-rise-edc-logo capitec-rise-edc-logo--full"
          />
          <img
            src={edcIcon}
            alt=""
            className="capitec-rise-edc-logo capitec-rise-edc-logo--icon"
          />
        </a>
      </animated.header>

      <section className="capitec-rise-hero capitec-rise-inner">
        <div className="capitec-rise-hero-copy">
          <animated.h1
            style={headingAnimation}
            className="capitec-rise-hero-heading"
          >
            Unleash your
            <br />
            creative future
          </animated.h1>
          <animated.p
            style={descriptionAnimation}
            className="capitec-rise-hero-sub"
          >
            Join Capitec’s 10-week youth accelerator powered by EDC to tackle
            live brand briefs, gain hands-on mentorship and turn your talent
            into real income.
          </animated.p>
          <animated.div style={buttonAnimation} className="capitec-rise-hero-button-row">
            <ApplyNowButton
              disabled={hasSubmitted}
              onClick={() => setIsApplyOpen(true)}
            />
          </animated.div>
        </div>
        <animated.div
          style={imageAnimation}
          className="capitec-rise-hero-media"
        >
          <img
            src={studentsImage}
            alt="Two young creators looking at their phones and smiling"
            className="capitec-rise-hero-image"
          />
        </animated.div>
      </section>

      <section className="capitec-rise-about">
        <div className="capitec-rise-inner capitec-rise-about-grid">
          <BounceIn className="capitec-rise-about-copy">
            <h2 className="capitec-rise-section-heading">
              What is Capitec Rise?
            </h2>
            <p>
              The Capitec Rise Program is a 10-week creator accelerator designed
              to help emerging creators grow their audiences, improve engagement,
              strengthen their personal brands and become commercially ready for
              brand partnerships.
            </p>
            <p>
              Through live learning sessions, practical assignments, mentorship
              and collaboration, participants will gain the skills and confidence
              needed to create high-performing content and unlock new
              opportunities within the creator economy.
            </p>
          </BounceIn>
          <BounceIn className="capitec-rise-benefits" delay={250}>
            <h2 className="capitec-rise-section-heading capitec-rise-section-heading--benefits">
              Program Benefits
            </h2>
            <ul className="capitec-rise-benefit-list">
              {BENEFITS.map(benefit => (
                <li key={benefit.alt} className="capitec-rise-benefit">
                  <img
                    src={benefit.icon}
                    alt=""
                    className="capitec-rise-benefit-icon"
                  />
                  <p className="capitec-rise-benefit-label">{benefit.label}</p>
                </li>
              ))}
            </ul>
          </BounceIn>
        </div>
      </section>

      <section className="capitec-rise-eligibility">
        <div className="capitec-rise-inner capitec-rise-eligibility-grid">
          <BounceIn className="capitec-rise-eligibility-col">
            <h3 className="capitec-rise-subheading">Who should apply?</h3>
            <p>
              We are looking for passionate emerging creators who are serious
              about growing their influence and building a sustainable creator
              career.
            </p>
            <p>Applicants should:</p>
            <ul>
              {APPLICANT_SHOULD.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </BounceIn>
          <BounceIn className="capitec-rise-eligibility-col" delay={250}>
            <p>Applicants must have:</p>
            <ul>
              {APPLICANT_MUST_HAVE.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>Powered by EDC Squared</p>
            <p>
              EDC Squared is the engine behind Capitec Rise, equipping young
              creators with the monetization technology, tools and industry
              curriculum needed to build commercial success. Visit{' '}
              <a href="/">edcsquared.io</a> to learn more.
              <br />
              Capitec Bank Limited FSP46669.
            </p>
          </BounceIn>
        </div>
      </section>

      <BounceIn className="capitec-rise-cta" as="section">
        <h2 className="capitec-rise-cta-heading">Ready to rise?</h2>
        <ApplyNowButton
          variant="on-blue"
          disabled={hasSubmitted}
          onClick={() => setIsApplyOpen(true)}
        />
      </BounceIn>
      <CapitecRiseApplyModal
        isOpen={isApplyOpen}
        onClose={() => setIsApplyOpen(false)}
        onSubmitted={() => setHasSubmitted(true)}
        onSuccess={() => {
          setIsApplyOpen(false);
          setShowConfirmation(true);
        }}
      />
      <CapitecRiseConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
      />
    </div>
  );
};

export default CapitecRise;
